/**
 * Flattens nested Experience theme tokens into SCREAMING_SNAKE keys.
 *
 * Authoring (content-i18n) may use:
 *   { COLOR_PRIMARY: "#...", components: { card: { radius: "16px" } }, features: { cases: { table: { rowZebraBg: "..." } } } }
 *
 * Wire format after flatten:
 *   COLOR_PRIMARY, COMPONENT_CARD_RADIUS, FEATURE_CASES_TABLE_ROW_ZEBRA_BG
 */
export type NestedThemeTokenValue = string | number | boolean | NestedThemeTokens | null | undefined;
export type NestedThemeTokens = { [key: string]: NestedThemeTokenValue };

const ROOT_NESTED_PREFIX: Record<string, string> = {
    components: 'COMPONENT',
    features: 'FEATURE',
    primitives: 'PRIMITIVE',
};

const toScreamingSnake = (segment: string): string =>
    segment
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/-/g, '_')
        .replace(/\s+/g, '_')
        .toUpperCase();

const isPlainObject = (value: unknown): value is NestedThemeTokens =>
    !!value && typeof value === 'object' && !Array.isArray(value);

/**
 * Converts nested theme token trees into flat Experience keys for CSS var mapping.
 * Top-level string tokens (COLOR_PRIMARY, TABLE_HEADER_BG, …) are preserved as-is.
 */
export const flattenThemeTokens = (
    tokens: NestedThemeTokens | Record<string, string> | null | undefined,
): Record<string, string> => {
    const out: Record<string, string> = {};

    const walk = (node: NestedThemeTokens, prefix: string): void => {
        for (const [key, value] of Object.entries(node)) {
            if (value === null || value === undefined || value === '') {
                continue;
            }

            if (isPlainObject(value)) {
                const rootPrefix = !prefix ? ROOT_NESTED_PREFIX[key] : undefined;
                const nextPrefix = rootPrefix
                    ? rootPrefix
                    : prefix
                      ? `${prefix}_${toScreamingSnake(key)}`
                      : toScreamingSnake(key);
                walk(value, nextPrefix);
                continue;
            }

            if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
                const flatKey = prefix ? `${prefix}_${toScreamingSnake(key)}` : toScreamingSnake(key);
                // Preserve already-screaming top-level keys (COLOR_PRIMARY stays COLOR_PRIMARY)
                const finalKey =
                    !prefix && key === key.toUpperCase() && key.includes('_') ? key : flatKey;
                out[finalKey] = String(value);
            }
        }
    };

    if (tokens) {
        walk(tokens as NestedThemeTokens, '');
    }
    return out;
};
